// data-surface-utils.ts — pure helpers + constants extracted from
// DataSurfaceV3.tsx. Zero React state; can be unit-tested in
// isolation. Audit finding CQ.1 / PERF.1: split DataSurfaceV3
// (3,400+ LOC) so each concern is a separate file.

import React from 'react';
import { TagChip } from '../../lib/tag-colors';

// ── Column-classification constants ──────────────────────────────────

/** Envelope columns are bare identifiers in BQ; payload paths go
 *  through JSON_VALUE. Used by group-by pivot, distribution drawer,
 *  and any future GROUP BY / aggregation feature. */
export const ENVELOPE_COLUMNS = new Set([
  'event_id', 'source_id', 'event_key', 'received_at',
  'parent_event_id', 'derivation_depth', 'trace_id',
]);

/** Top-level event-envelope columns that don't make sense as `eq`
 *  filters within a per-source view: source_id is constant, hash IDs
 *  are unique per row (filter would always narrow to 1), payload + origin
 *  are JSON aggregates that need a different drill path. received_at
 *  is excluded because timestamps are sub-second unique.
 *  input_tags is a JSON object; input_name is on origin not payload.
 */
export const NON_FILTERABLE_CELLS = new Set([
  'source_id', 'event_key', 'parent_event_id', 'received_at',
  'derivation_depth', 'trace_id', 'payload', 'origin',
  'input_tags', 'input_name',
]);

/** Columns whose values are hashes — display only the first 12 chars
 *  + ellipsis. Tooltip carries the full value. */
export const HASH_COLUMNS = new Set(['event_id', 'event_key', 'parent_event_id', 'trace_id', '_fingerprint']);

// ── Schema fingerprint (per-row) ─────────────────────────────────────
//
// Mirrors the server-side fingerprint algorithm in views-auto-suggest
// (services/app/src/routes/sources/views-auto-suggest.ts). Two rows
// share a fingerprint when their payload leaf paths collapse to the
// same canonical set after hash-segment normalisation. Operators use
// this to:
//   - SEE which event belongs to which structural group, per row
//   - FILTER to rows of just one group with one click
// Computed client-side from the raw `payload` JSON so we don't pay a
// BQ round-trip or a new SELECT expression. ~50µs per row in JS.

const FP_SAFE_KEY = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
const FP_HASH_SEG = /^[a-f0-9]{16,}$/i;
const FP_UUID_SEG = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
const FP_MAX_DEPTH = 5;

function fpLeafPaths(payload: Record<string, unknown>, prefix = '', out: string[] = [], depth = 0): string[] {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload) || depth > FP_MAX_DEPTH) {
    if (prefix) out.push(prefix);
    return out;
  }
  for (const [k, v] of Object.entries(payload)) {
    if (!FP_SAFE_KEY.test(k)) continue;
    const path = prefix ? `${prefix}.${k}` : k;
    const isObj = v !== null && typeof v === 'object' && !Array.isArray(v);
    if (isObj && depth < FP_MAX_DEPTH) fpLeafPaths(v as Record<string, unknown>, path, out, depth + 1);
    else out.push(path);
  }
  return out;
}

function fpNormalize(path: string): string {
  if (path === 'previous' || path.startsWith('previous.') || path.startsWith('previous__')) return 'previous';
  const tokens: string[] = [];
  const sep: string[] = [];
  let buf = '';
  for (let i = 0; i < path.length; i++) {
    const ch = path[i];
    if (ch === '.') { tokens.push(buf); buf = ''; sep.push('.'); }
    else if (ch === '_' && path[i + 1] === '_') { tokens.push(buf); buf = ''; sep.push('__'); i++; }
    else buf += ch;
  }
  tokens.push(buf);
  const out: string[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i]!;
    if (FP_HASH_SEG.test(t) || FP_UUID_SEG.test(t)) {
      out.push('*');
      return out.reduce((a, tok, idx) => a + (idx > 0 ? sep[idx - 1] ?? '__' : '') + tok, '');
    }
    out.push(t);
  }
  return out.reduce((a, tok, idx) => a + (idx > 0 ? sep[idx - 1] ?? '.' : '') + tok, '');
}

// Cheap non-crypto hash → 12-char hex. Same SHA-input → same output.
// Crypto SHA256 is overkill for a visual ID; FNV-1a in JS gives stable
// 32-bit hashes we just hex-pad. Two-pass + length-mix collision risk
// on 12 chars: ~1 in 16M per group — fine for visual disambiguation
// across the 200-row preview.
function fpHash12(s: string): string {
  let h1 = 2166136261 >>> 0;
  let h2 = (s.length * 16777619) >>> 0;
  for (let i = 0; i < s.length; i++) {
    h1 ^= s.charCodeAt(i);
    h1 = (h1 * 16777619) >>> 0;
    h2 ^= s.charCodeAt(s.length - 1 - i);
    h2 = (h2 * 16777619) >>> 0;
  }
  return (h1.toString(16).padStart(8, '0') + h2.toString(16).padStart(8, '0')).slice(0, 12);
}

/** Public entry: compute the operator-visible group id from a payload.
 *  Returns `grp_<12hex>` (matches the server's gid() format). Empty
 *  string when payload has no extractable paths (so caller can hide
 *  the cell rather than showing a meaningless badge). */
export function computeRowFingerprint(payload: unknown): string {
  if (!payload || typeof payload !== 'object') return '';
  const paths = fpLeafPaths(payload as Record<string, unknown>).sort();
  if (paths.length === 0) return '';
  const norm = [...new Set(paths.map(fpNormalize))].sort();
  return `grp_${fpHash12(norm.join('|'))}`;
}

// ── Pretty fingerprint label (color-noun fallback) ───────────────────
//
// Operator-facing display for fingerprint cells: instead of showing
// `a3f12b4c8d0e` (memorable to nobody), we generate a `<color>-<noun>`
// label deterministically from the fingerprint hash. The color half
// matches the chip's hue so visual + verbal identity stays paired
// (the violet chip reads "violet-river"; the amber chip reads
// "amber-tide"). 12 colors × 56 nouns = 672 combinations — far more
// than any source's distinct fingerprint count.

const FP_COLORS = [
  'ruby', 'amber', 'moss', 'fern', 'jade', 'sky',
  'ocean', 'indigo', 'violet', 'plum', 'rose', 'coral',
] as const;

const FP_NOUNS = [
  'arrow', 'bay', 'bird', 'breeze', 'cloud', 'cove',
  'creek', 'crest', 'dale', 'dawn', 'dew', 'dune',
  'fern', 'fjord', 'fog', 'forest', 'glen', 'grove',
  'gull', 'haven', 'hill', 'horizon', 'isle', 'lake',
  'leaf', 'meadow', 'mist', 'moon', 'mountain', 'oasis',
  'orchard', 'palm', 'path', 'pebble', 'peak', 'pine',
  'pond', 'rain', 'reef', 'river', 'rock', 'shore',
  'spring', 'star', 'stone', 'storm', 'stream', 'sun',
  'thicket', 'tide', 'trail', 'tree', 'tundra', 'valley',
  'wind', 'wood',
] as const;

/** Two-word label derived deterministically from a `grp_<hex>`
 *  fingerprint. Same fingerprint → same label, always. The color half
 *  is keyed off the same hue we use for the chip background so
 *  operator can pattern-match: "the violet group" reads "violet-river"
 *  in tag chips elsewhere. */
export function prettyFingerprintLabel(fp: string): string {
  if (!fp || !fp.startsWith('grp_')) return fp;
  const hex = fp.slice(4);
  // Color from first 3 hex chars (matches the chip's hue derivation).
  const colorIdx = parseInt(hex.slice(0, 3), 16) % FP_COLORS.length;
  // Noun from a later slice so adjacent fingerprints don't share both.
  const nounIdx = parseInt(hex.slice(6, 9), 16) % FP_NOUNS.length;
  return `${FP_COLORS[colorIdx]}-${FP_NOUNS[nounIdx]}`;
}

/** Resolve a fingerprint to its operator-facing display label.
 *  Lookup order:
 *    1. Saved SchemaDefinition whose `source_fingerprints` includes
 *       this group id → use `schema.name` (operator-given name wins).
 *    2. Otherwise → fall back to the deterministic `color-noun` label
 *       so the chip carries a memorable identity even before the
 *       operator promotes the group to a named schema.
 *  Pass the schemas map computed by the caller (Source-detail page
 *  already fetches /sources/:id/schemas for the DetectedModelsPanel,
 *  so the data is free).
 */
export function resolveFingerprintLabel(
  fp: string,
  schemaByFingerprint?: Map<string, { name: string }>,
): { label: string; isNamed: boolean } {
  if (!fp) return { label: '', isNamed: false };
  const named = schemaByFingerprint?.get(fp);
  if (named?.name) return { label: named.name, isNamed: true };
  return { label: prettyFingerprintLabel(fp), isNamed: false };
}

/** Columns where ISO timestamps render as `HH:MM:SS · 2m ago` for
 *  events ingested in the last hour, full local datetime otherwise. */
export const TIMESTAMP_COLUMNS = new Set(['received_at']);

/** Compact-mode operator labels for the filter chips. */
export const OP_SYMBOL: Record<string, string> = {
  is_present: 'is set',
  is_empty:   'is empty',
  eq:         '=',
  neq:        '≠',
  gt:         '>',
  lt:         '<',
  contains:   '⊇',
};

/** Classify a column into one of three buckets so the Select Fields
 *  drawer can show an at-a-glance origin chip. `system` covers
 *  hash-IDs and trace metadata; `envelope` is the canonical event
 *  shell (payload + origin object); everything else is `payload`. */
export type FieldOrigin = 'payload' | 'envelope' | 'system';
export const SYSTEM_FIELDS = new Set(['event_key', 'parent_event_id', 'derivation_depth', 'trace_id']);
export const ENVELOPE_FIELDS = new Set(['event_id', 'source_id', 'received_at', 'payload', 'origin']);
export function fieldOrigin(col: string): FieldOrigin {
  if (SYSTEM_FIELDS.has(col)) return 'system';
  if (ENVELOPE_FIELDS.has(col)) return 'envelope';
  return 'payload';
}

// ── SQL helpers ──────────────────────────────────────────────────────

/** SQL expression for a column reference. Envelope columns are bare
 *  identifiers; payload paths go through JSON_VALUE. */
export function fieldExpr(field: string): string {
  if (ENVELOPE_COLUMNS.has(field)) return `\`${field}\``;
  return `JSON_VALUE(payload, '$.${field}')`;
}

/** Convert a single FilterRow to a BQ WHERE predicate. Mirrors
 *  recipe-sql.ts:filterToSql but lives here so the SQL generator can
 *  apply filters at the FROM-wrap level — keeping the auto-projected
 *  SELECT (flat payload columns) intact. */
export interface FilterRow {
  field_path: string;
  op: 'is_present' | 'is_empty' | 'eq' | 'neq' | 'gt' | 'lt' | 'contains';
  value: string;
}
/** Top-level columns of the `events` BQ table (NOT JSON). Kept in
 *  sync with recipe-sql.ts:SYSTEM_COLUMNS so the two predicate
 *  builders agree on what's a column vs a payload path. */
const V3_SYSTEM_COLUMNS = new Set([
  'event_id', 'event_key', 'source_id', 'received_at',
  'derivation_depth', 'parent_event_id',
]);

/** Resolve a field path to a SQL value-expression — same three-branch
 *  shape as recipe-sql.ts. Keeps `$origin.*` meta-paths and system
 *  columns out of the JSON_VALUE(payload, …) wrap so view filters can
 *  target them without manual SQL-by-hand. */
function v3FieldExpr(path: string): string {
  if (path.startsWith('$origin.')) {
    return `JSON_VALUE(origin, '$.${path.slice('$origin.'.length)}')`;
  }
  if (V3_SYSTEM_COLUMNS.has(path)) {
    return `CAST(${path} AS STRING)`;
  }
  return `JSON_VALUE(payload, '$.${path}')`;
}

export function filterRowToPredicate(f: FilterRow): string | null {
  if (!f.field_path.trim()) return null;
  const path = f.field_path.trim();
  const v = f.value;
  const sqlString = (s: string): string => `'${s.replace(/'/g, "''")}'`;
  const sqlNumber = (s: string): string => Number.isFinite(Number(s)) ? String(Number(s)) : '0';
  const expr = v3FieldExpr(path);
  switch (f.op) {
    case 'is_present': return `${expr} IS NOT NULL`;
    case 'is_empty':   return `(${expr} IS NULL OR ${expr} = '')`;
    case 'eq':         return `${expr} = ${sqlString(v)}`;
    case 'neq':        return `${expr} != ${sqlString(v)}`;
    case 'gt':         return `CAST(${expr} AS FLOAT64) > ${sqlNumber(v)}`;
    case 'lt':         return `CAST(${expr} AS FLOAT64) < ${sqlNumber(v)}`;
    case 'contains':   return `STRPOS(LOWER(${expr}), LOWER(${sqlString(v)})) > 0`;
  }
}

/** Parse column aliases out of a SELECT … FROM statement. Used by
 *  the tab tooltip (KPI #8 — no-query schema preview). */
export function extractColumnNamesFromSql(sql: string): string[] {
  if (!sql) return [];
  const flat = sql
    .replace(/--[^\n]*/g, '')
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .replace(/\s+/g, ' ');
  const m = flat.match(/SELECT\s+(.*?)\s+FROM\s+/i);
  if (!m || !m[1]) return [];
  const projection = m[1];
  if (projection.trim() === '*') return [];
  const cols: string[] = [];
  let depth = 0;
  let buf = '';
  for (const ch of projection) {
    if (ch === '(') depth++;
    else if (ch === ')') depth--;
    else if (ch === ',' && depth === 0) {
      cols.push(buf.trim()); buf = ''; continue;
    }
    buf += ch;
  }
  if (buf.trim()) cols.push(buf.trim());
  return cols.map(c => {
    const am = c.match(/\s+AS\s+`?([a-zA-Z_][a-zA-Z0-9_]*)`?$/i);
    if (am && am[1]) return am[1];
    const bm = c.match(/^`?([a-zA-Z_][a-zA-Z0-9_]*)`?$/);
    if (bm && bm[1]) return bm[1];
    return c.length > 24 ? c.slice(0, 21) + '…' : c;
  }).filter(c => c.length > 0);
}

// ── Cell formatting ──────────────────────────────────────────────────

export function fmtRelative(ms: number): string {
  if (ms < 0) return '';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function formatCell(v: unknown, column?: string): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') {
    if (column && HASH_COLUMNS.has(column) && v.length > 16) {
      return `${v.slice(0, 12)}…`;
    }
    if (column && TIMESTAMP_COLUMNS.has(column) && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) {
      const d = new Date(v);
      if (!Number.isNaN(d.getTime())) {
        const ms = Date.now() - d.getTime();
        const time = d.toLocaleTimeString();
        if (ms < 60 * 60 * 1000 && ms >= 0) return `${time} · ${fmtRelative(ms)}`;
        return d.toLocaleString();
      }
    }
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) {
      const d = new Date(v);
      if (!Number.isNaN(d.getTime())) return d.toLocaleString();
    }
    return v.length > 120 ? v.slice(0, 117) + '…' : v;
  }
  if (typeof v === 'object') return JSON.stringify(v).slice(0, 120);
  return String(v);
}

/** Does this cell value get truncated by formatCell? Drives whether we
 *  show the hover-preview popover. Short scalars never trigger it. */
export function isCellTruncated(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === 'object') return true;
  if (typeof v === 'string') {
    if (v.length > 120) return true;
    if ((v.startsWith('{') && v.endsWith('}')) || (v.startsWith('[') && v.endsWith(']'))) {
      return v.length > 40;
    }
    return false;
  }
  return false;
}

/** Pretty-print a cell value for the hover popover. Objects / arrays
 *  go through JSON.stringify with 2-space indent. */
export function formatPreview(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v, null, 2);
  if (typeof v === 'string') {
    if ((v.startsWith('{') && v.endsWith('}')) || (v.startsWith('[') && v.endsWith(']'))) {
      try { return JSON.stringify(JSON.parse(v), null, 2); } catch { /* fall through */ }
    }
    return v;
  }
  return String(v);
}

/** Wrap any case-insensitive matches of `term` inside the cell text
 *  with a <mark> for visual highlighting. Escapes regex metachars. */
export function renderHighlight(text: string, term: string): React.ReactNode {
  if (!term || !text) return text;
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return parts.map((part, i) =>
    i % 2 === 1
      ? React.createElement('mark', { key: i, style: { background: 'oklch(0.92 0.15 95)', color: 'var(--ink)', padding: '0 1px', borderRadius: 2 } }, part)
      : React.createElement(React.Fragment, { key: i }, part)
  );
}

/** Render origin.input_tags as a chip row. Accepts stringified or
 *  parsed object. Up to 3 chips inline + '+N' overflow.
 *  Chips colour-coded per-(key,value) via TagChip — operators can
 *  distinguish entity=person from entity=deal at a glance. */
export function renderInputTags(v: unknown): React.ReactNode {
  let obj: Record<string, string> | null = null;
  if (typeof v === 'string') {
    try { obj = JSON.parse(v) as Record<string, string>; } catch { return v; }
  } else if (v && typeof v === 'object') {
    obj = v as Record<string, string>;
  }
  if (!obj || typeof obj !== 'object') return null;
  const entries = Object.entries(obj);
  if (entries.length === 0) return null;
  const visible = entries.slice(0, 3);
  const overflow = entries.length - visible.length;
  const fullText = entries.map(([k, val]) => `${k}=${val}`).join(' · ');
  return React.createElement(
    'span',
    { title: fullText, style: { display: 'inline-flex', gap: 4, flexWrap: 'wrap' } },
    [
      ...visible.map(([k, val]) =>
        React.createElement(TagChip, { key: k, k, v: String(val), compact: true })
      ),
      overflow > 0
        ? React.createElement('span', { key: '_o', style: { fontSize: 10, color: 'var(--ink-3)' } }, `+${overflow}`)
        : null,
    ]
  );
}

// ── Export helpers ──────────────────────────────────────────────────

/** Trigger a browser download of preview rows as either CSV or NDJSON.
 *  Bounded by the in-memory preview cap (50 rows by default). For
 *  full exports use the BQ extract job (BACKLOG). */
export function exportPreview(
  rows: Array<Record<string, unknown>>,
  viewName: string,
  format: 'csv' | 'json',
  toast: { show: (msg: string, opts?: { kind?: 'success' | 'error' | 'info' | 'warning' }) => void },
): void {
  if (rows.length === 0) return;
  const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  const filename = `${viewName}_${ts}.${format === 'csv' ? 'csv' : 'ndjson'}`;
  let body: string;
  if (format === 'json') {
    body = rows.map(r => JSON.stringify(r)).join('\n');
  } else {
    const cols = Object.keys(rows[0]!);
    const escape = (v: unknown): string => {
      if (v === null || v === undefined) return '';
      const s = typeof v === 'string' ? v : typeof v === 'object' ? JSON.stringify(v) : String(v);
      if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const header = cols.join(',');
    const data = rows.map(r => cols.map(c => escape(r[c])).join(','));
    body = [header, ...data].join('\n');
  }
  const blob = new Blob([body], { type: format === 'csv' ? 'text/csv' : 'application/x-ndjson' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  toast.show(`Exported ${rows.length} row${rows.length === 1 ? '' : 's'} → ${filename}`, { kind: 'success' });
}

/** Format the visible preview rows as a GitHub-flavoured Markdown
 *  table and copy to clipboard. Operators paste into Slack / Notion /
 *  GitHub issues. Cells truncated to 80 chars + pipes escaped. */
export function copyPreviewAsMarkdown(
  rows: Array<Record<string, unknown>>,
  toast: { show: (msg: string, opts?: { kind?: 'success' | 'error' | 'info' | 'warning' }) => void },
): void {
  if (rows.length === 0) return;
  const cols = Object.keys(rows[0]!);
  const cell = (v: unknown): string => {
    if (v === null || v === undefined) return '';
    const s = typeof v === 'string' ? v : typeof v === 'object' ? JSON.stringify(v) : String(v);
    const safe = s.replace(/\|/g, '\\|').replace(/\n/g, ' ');
    return safe.length > 80 ? `${safe.slice(0, 77)}…` : safe;
  };
  const header = `| ${cols.join(' | ')} |`;
  const separator = `| ${cols.map(() => '---').join(' | ')} |`;
  const data = rows.map(r => `| ${cols.map(c => cell(r[c])).join(' | ')} |`);
  const md = [header, separator, ...data].join('\n');
  void navigator.clipboard.writeText(md);
  toast.show(`Copied ${rows.length} row${rows.length === 1 ? '' : 's'} as Markdown`, { kind: 'success' });
}

// ── Multi-row selection helper ──────────────────────────────────────

/** Multi-row selection only makes sense when each row has a stable
 *  event_id — so the checkbox column hides on auto-projected payload-
 *  only views without it (rare but possible). */
export function hasEventIdColumn(cols: string[]): boolean {
  return cols.includes('event_id');
}

// ── Sample-value formatter (Select Fields drawer) ─────────────────

export function formatSampleValue(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v.length > 50 ? `${v.slice(0, 47)}…` : v;
  if (typeof v === 'object') {
    const s = JSON.stringify(v);
    return s.length > 50 ? `${s.slice(0, 47)}…` : s;
  }
  return String(v);
}

/** Filter predicate for the Select Fields drawer. */
export function matchFieldFilter(
  name: string,
  type: string,
  origin: FieldOrigin,
  filter: string,
  mode: 'any' | 'name' | 'type' | 'origin',
): boolean {
  const q = filter.trim().toLowerCase();
  if (!q) return true;
  if (mode === 'name') return name.toLowerCase().includes(q);
  if (mode === 'type') return type.toLowerCase().includes(q);
  if (mode === 'origin') return origin.toLowerCase().includes(q);
  return name.toLowerCase().includes(q)
    || type.toLowerCase().includes(q)
    || origin.toLowerCase().includes(q);
}

// ── Style constants ──────────────────────────────────────────────────

export const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  fontFamily: 'var(--mono)',
  fontSize: 12,
  border: '1px solid var(--line)',
  borderRadius: 4,
  background: 'var(--bg)',
  color: 'var(--ink)',
};

export const ctxMenuItemStyle: React.CSSProperties = {
  textAlign: 'left',
  background: 'transparent',
  border: 'none',
  padding: '6px 10px',
  fontSize: 12,
  fontFamily: 'var(--ui)',
  color: 'var(--ink)',
  cursor: 'pointer',
  borderRadius: 3,
};

export const tabMenuItemStyle: React.CSSProperties = {
  textAlign: 'left',
  background: 'transparent',
  border: 'none',
  padding: '6px 10px',
  fontSize: 12,
  fontFamily: 'var(--ui)',
  color: 'var(--ink)',
  cursor: 'pointer',
  borderRadius: 3,
};

export const fieldsThStyle: React.CSSProperties = {
  padding: '8px 8px',
  textAlign: 'left',
  borderBottom: '1px solid var(--line)',
  fontFamily: 'var(--mono)',
  fontSize: 10.5,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'var(--ink-3)',
  fontWeight: 500,
};

export const fieldTypeChipStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '1px 6px',
  fontFamily: 'var(--mono)',
  fontSize: 10,
  background: 'var(--bg-sunken)',
  border: '1px solid var(--line)',
  borderRadius: 2,
  color: 'var(--ink-3)',
  textTransform: 'lowercase',
};

export const fieldOriginChipStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '1px 8px',
  fontFamily: 'var(--mono)',
  fontSize: 10,
  borderRadius: 8,
  textTransform: 'lowercase',
};
